import { Product } from "@omni/types/api/product/bff/product.model";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { isDefined } from "@omni/utils/helper.utils";
import { AxiosResponse } from "axios";
import { isEmpty } from "lodash-es";
import { useCallback, useEffect, useState } from "react";

import { useBFFProductService } from "@/api/hooks/productService.hook";
import { ProductSliderComponent } from "@/components/Product/ProductSlider/productSlider.component";

import { useProductSliderContext } from "../productSlider.context";
import { ProductSliderSkeletonComponent } from "../skeleton/productSliderSkeleton.component";

export type MainProductSliderProps = {
    sku: string;
};

const MainProductSlider = ({ sku }: MainProductSliderProps) => {
    const { prefetchedProducts } = useProductSliderContext();

    const [products, setProducts] = useState(prefetchedProducts);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    const productService = useBFFProductService();

    const fetchAccessoryProduct = useCallback(
        (sku: string) => {
            return productService
                .get<{}, AxiosResponse<Array<Product<ProductFieldSet.Full>>>>(
                    "/products",
                    {
                        params: {
                            sku: sku,
                            fields: ProductFieldSet.Full
                        }
                    }
                )
                .then(({ data }) => data.at(0));
        },
        [productService.get]
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        if (!sku) return;
        if (isDefined(products)) return;
        if (loading || error) return;

        setLoading(true);

        fetchAccessoryProduct(sku)
            .then((product) => product?.accessoryParents ?? [])
            .then(setProducts)
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, [
        products,
        sku,
        loading,
        error,
        setLoading,
        fetchAccessoryProduct,
        setProducts
    ]);

    if (!sku || error) {
        return null;
    } else if (isDefined(products) && isEmpty(products)) {
        return null;
    } else if (isDefined(products) && !isEmpty(products)) {
        return <ProductSliderComponent products={products} />;
    } else {
        return <ProductSliderSkeletonComponent />;
    }
};

export { MainProductSlider };
